// El mundo de la oficina (S11, docs/oficina-v2/S11-BRIEF-DISENO.md §5 + crítica C2/C3). Función PURA:
// tamaño de la ventana + salas + contratados → la planta, en «píxeles de arte» (la escena amplía por el
// zoom entero `z`, nunca menor que 2).
//
// Composición: la gente trabaja en ISLAS de 4 mesas enfrentadas (2 arriba mirando hacia abajo, 2 abajo),
// agrupadas por sala sobre su alfombra; debajo, la zona común (reuniones · sofás · cocina · recepción junto
// a la puerta). Ese bloque se centra en el área VISIBLE (a la derecha del panel flotante del equipo) y el
// suelo, las ventanas y las plantas llenan el resto de la ventana. Si el bloque no cabe a zoom 2, el mundo
// crece y se desplaza (nunca se encoge a 1×).

import { huecosVigentes, topeMesas, totalPuestos } from '../compartido/mesas';

export interface Rect { x: number; y: number; w: number; h: number }
export interface Punto { x: number; y: number }

export const CELDA = { w: 72, h: 82 } as const; // un puesto dentro de una isla
export const ISLA = { w: CELDA.w * 2, h: CELDA.h * 2 } as const;
const PASILLO_X = 40; // entre islas
const PASILLO_X_COMPACTO = 24;
const PASILLO_Y = 36;
const PASILLO_Y_COMPACTO = 12;
const RELLENO_ZONA = 14; // alfombra alrededor de las islas
const RELLENO_ZONA_COMPACTO = 8;
const LETRERO = 6; // aire dentro de la alfombra, arriba (el letrero va FUERA, encima: crítica N6)
const ALTO_LETRERO = 18; // franja sobre la alfombra para el letrero
export const PARED = 44; // pared superior con ventanales
export const ZOCALO = 14; // pared inferior (la puerta está en ella)
const AIRE = 16; // entre el bloque de la oficina y los bordes del área visible
const AIRE_COMPACTO = 4;
// 04-11 — alto de la fila de los que se van (cabeza a pies, sobre el zócalo): el bloque deja ese hueco debajo si cabe.
const FILA_SALIDA = 52;
const ALTO_COMUN = 120; // S13: con 104 el letrero de la recepción pisaba la cocina

export interface SalaMundo {
  projectId: string;
  nombre: string;
  color: string;
  principal: boolean;
  /** 09-10 — mesas que dejó Pau a mano (null o ausente = automático) y puestos quitados (compartido/mesas.ts). */
  mesas?: number | null;
  huecos?: readonly number[];
}
export interface ContratadoMundo { agentId: string; projectId: string; mesa: number }

export interface ZonaMundo { projectId: string; nombre: string; color: string; rect: Rect; letrero: Punto }

export interface MesaMundo {
  projectId: string;
  indice: number;
  /** Celda del puesto (para la capa DOM: pulsar, etiqueta). */
  rect: Rect;
  /** Donde se sienta la persona (centro del asiento). */
  asiento: Punto;
  /** Fila de arriba de la isla: la persona mira hacia abajo, a su mesa. */
  arriba: boolean;
  agentId: string | null;
  /** La primera libre de la sala: el «+» para contratar ahí. */
  libre: boolean;
  /** 09-10 — puesto que Pau quitó: ni mesa ni silla (se puede volver a poner). */
  hueco: boolean;
  /** 09-10 — sitio libre al final de la última isla, aún sin contar como puesto (p. ej. el 4.º de una isla de 3):
   *  también es `hueco` (no hay mesa), pero pulsarlo añade la mesa ahí. */
  vacio?: boolean;
}

export type TipoDecor = 'reuniones' | 'sofas' | 'cocina' | 'planta' | 'recepcion';
export interface DecorMundo { tipo: TipoDecor; rect: Rect }

export interface Mundo {
  z: number;
  ancho: number;
  alto: number;
  /** Área visible (a la derecha del panel), en píxeles de arte. */
  visible: Rect;
  zonas: ZonaMundo[];
  islas: Rect[];
  mesas: MesaMundo[];
  decor: DecorMundo[];
  /** null en la oficina compacta (mucha gente: no hay zona común). */
  recepcion: Rect | null;
  puerta: Punto;
  /**
   * 29-10 (Pau: «cuando un agente se esté yendo, que se ponga en un sitio separado… de fondo»): los que se van no
   * ocupan mesa; esperan en fila junto a la salida, apagados.
   */
  salida: Array<{ agentId: string; asiento: Punto }>;
  /** El rectángulo que encuadra la cámara (la oficina ocupada), para centrar el desplazamiento. */
  encuadre: Rect;
  /**
   * El bloque de la oficina NO cabe en la ventana y hay que desplazarse. Si es false, el mundo solo pasa
   * de la ventana por el redondeo del zoom (hasta z-1 px): eso se recorta, nunca se desplaza — una barra de
   * desplazamiento por 1 px cambia el hueco medido y el mundo entraba en bucle (parpadeo, 28-09).
   */
  desborda: boolean;
}

export interface EntradaMundo {
  anchoPx: number;
  altoPx: number;
  /** Lo que flota a la izquierda (panel del equipo + márgenes), en píxeles de pantalla. */
  panelIzqPx: number;
  salas: SalaMundo[];
  contratados: ContratadoMundo[];
  /** 29-10 — los que se van (sin mesa): van a la fila de la salida, en este orden. */
  saliendo?: string[];
}

/** Una sala ya medida: cuántos puestos y cuántas islas lleva, y en qué rejilla (columnas × filas) las pone. */
interface PlanoSala { sala: SalaMundo; puestos: number; islas: number; cols: number; w: number; h: number; media: boolean }
/** Dónde va cada cosa dentro del bloque de la oficina (coordenadas relativas a su esquina). */
interface Colocada { p: PlanoSala; x: number; y: number }
/** «rincón»: solo los sofás, para el hueco donde no entra la zona común entera (pantallas 2K a zoom 3). */
type FormaComun = 'tumbada' | 'de_pie' | 'rincon';

interface Plano {
  salas: Colocada[];
  /** La zona común (reuniones · sofás · cocina · recepción), si cabe; null en la oficina compacta o sin hueco. */
  comun: { forma: FormaComun; x: number; y: number; w: number; h: number } | null;
  w: number;
  h: number;
  /** Con mucha gente: pasillos más estrechos, para que todos entren en pantalla. */
  compacto: boolean;
}

// S13 (06-10, Pau: «mucho espacio vacío»): las salas ya no se apilan en una columna. Con 3 salas, la columna
// medía el triple de la pantalla y los lados quedaban de suelo desnudo. Ahora la principal va a la izquierda y
// las demás en columnas a su derecha; la zona común entra en el hueco que quede (de pie o tumbada).
const HUECO_SALAS = 24; // entre salas, en las dos direcciones
const HUECO_SALAS_COMPACTO = 16;
const COMUN: Record<FormaComun, { w: number; h: number }> = { tumbada: { w: 460, h: ALTO_COMUN }, de_pie: { w: 294, h: 164 }, rincon: { w: 132, h: 88 } };

/**
 * Puestos de una sala (compartido/mesas.ts): en automático, la gente + al menos 2 libres, en islas de 4 con media isla
 * final si sobran 1-2 (Pau, 06-10: «mucho espacio vacío»); a mano, los que dejó Pau. La última isla lleva el resto
 * (1-4 puestos) y es MEDIA (una fila) si lleva 1 o 2.
 */
function puestosDe(sala: SalaMundo, ocupadas: ContratadoMundo[]): { puestos: number; islas: number; media: boolean } {
  const puestos = totalPuestos(ocupadas.map((c) => c.mesa), { principal: sala.principal, fijadas: sala.mesas ?? null, huecos: sala.huecos ?? [] });
  const islas = Math.ceil(puestos / 4);
  return { puestos, islas, media: puestos - (islas - 1) * 4 <= 2 };
}

/** La alfombra alrededor de las islas: más estrecha en la oficina compacta (la de pasillos estrechos). */
const rellenoDe = (pasilloX: number) => (pasilloX === PASILLO_X_COMPACTO ? RELLENO_ZONA_COMPACTO : RELLENO_ZONA);

function medirSala(sala: SalaMundo, puestos: number, islas: number, cols: number, pasilloY: number, pasilloX: number, media = false): PlanoSala {
  const filas = Math.ceil(islas / cols);
  // La última fila mide media isla si solo lleva la media (p. ej. una sala vacía: 1 media isla).
  const ultimaMedia = media && (cols === 1 || islas % cols === 1);
  return {
    sala,
    puestos,
    islas,
    cols,
    media,
    w: cols * ISLA.w + (cols - 1) * pasilloX + 2 * rellenoDe(pasilloX),
    h: ALTO_LETRERO + LETRERO + filas * ISLA.h - (ultimaMedia ? CELDA.h : 0) + (filas - 1) * pasilloY + rellenoDe(pasilloX),
  };
}

/** Rejillas posibles para n islas, de la más cuadrada a la más alargada; solo las que no dejan una fila a medias
 *  salvo que no haya otra (crítica N5: nunca un cuadrante vacío). Con `conFilaAMedias`, todas: el último recurso
 *  cuando con filas llenas la oficina no cabe (10-10, ver generarMundo). */
function rejillas(n: number, conFilaAMedias = false): number[] {
  const todas = Array.from({ length: n }, (_, i) => i + 1);
  const llenas = todas.filter((c) => n % c === 0 || c === n);
  const elegibles = llenas.length > 1 && !conFilaAMedias ? llenas : todas;
  return elegibles.sort((a, b) => Math.abs(a - Math.ceil(n / a)) - Math.abs(b - Math.ceil(n / b)) || b - a);
}

/**
 * Todas las distribuciones razonables del bloque: la principal con cada rejilla y las demás salas apiladas
 * debajo (como antes) o repartidas en 1..N columnas a su derecha. La zona común, donde quepa sin ensanchar.
 */
function candidatos(e: EntradaMundo, compacto: boolean, conFilaAMedias = false): Plano[] {
  const pasilloY = compacto ? PASILLO_Y_COMPACTO : PASILLO_Y;
  const pasilloX = compacto ? PASILLO_X_COMPACTO : PASILLO_X;
  const hueco = compacto ? HUECO_SALAS_COMPACTO : HUECO_SALAS;
  const ordenadas = [...e.salas].sort((a, b) => Number(b.principal) - Number(a.principal) || a.nombre.localeCompare(b.nombre));
  const gente = ordenadas.map((s) => e.contratados.filter((c) => c.projectId === s.projectId));
  const puestos = gente.map((g, i) => puestosDe(ordenadas[i]!, g));
  const islas = puestos.map((p) => p.islas);
  if (!ordenadas.length) return [{ salas: [], comun: compacto ? null : { forma: 'tumbada', x: 0, y: 0, ...COMUN.tumbada }, w: COMUN.tumbada.w, h: compacto ? 0 : COMUN.tumbada.h, compacto }];
  const [principal, ...resto] = ordenadas;
  const medirResto = (colsDe: (n: number) => number) =>
    resto.map((s, i) => medirSala(s, puestos[i + 1]!.puestos, islas[i + 1]!, colsDe(islas[i + 1]!), pasilloY, pasilloX, puestos[i + 1]!.media));
  // Las demás salas, con su rejilla preferida; con filas a medias, además, todas con a lo sumo 1, 2, 3… islas por
  // fila (estrechas para ir en una columna al lado de la principal, anchas para ir debajo sin crecer hacia abajo).
  const variantesResto = [medirResto((n) => rejillas(n)[0]!)];
  if (conFilaAMedias) for (let tope = 1; tope <= Math.max(1, ...islas.slice(1)); tope += 1) variantesResto.push(medirResto((n) => Math.min(n, tope)));
  const salida: Plano[] = [];
  for (const cols of rejillas(islas[0]!, conFilaAMedias)) {
    const p = medirSala(principal!, puestos[0]!.puestos, islas[0]!, cols, pasilloY, pasilloX, puestos[0]!.media);
    const combinaciones = variantesResto.flatMap((r) => Array.from({ length: resto.length + 1 }, (_, columnas) => [r, columnas] as const));
    for (const [restoMedidas, columnas] of combinaciones) {
      const salas: Colocada[] = [{ p, x: 0, y: 0 }];
      let w: number;
      let h: number;
      // Huecos libres del bloque (rectángulos) donde podría ir la zona común sin ensanchar nada.
      const huecos: Rect[] = [];
      if (columnas === 0) {
        // Todas apiladas, centradas sobre la principal (la distribución de siempre: buena con 1 sala).
        w = Math.max(p.w, ...restoMedidas.map((r) => r.w));
        h = p.h;
        salas[0]!.x = Math.round((w - p.w) / 2);
        for (const r of restoMedidas) {
          salas.push({ p: r, x: Math.round((w - r.w) / 2), y: h + pasilloY });
          h += pasilloY + r.h;
        }
      } else {
        // Columnas a la derecha de la principal: cada sala a la columna más baja (equilibrio).
        const cols: Array<{ salas: PlanoSala[]; w: number; h: number }> = Array.from({ length: columnas }, () => ({ salas: [], w: 0, h: 0 }));
        for (const r of restoMedidas) {
          const baja = cols.reduce((a, b) => (b.h < a.h ? b : a));
          baja.h += (baja.salas.length ? hueco : 0) + r.h;
          baja.w = Math.max(baja.w, r.w);
          baja.salas.push(r);
        }
        if (cols.some((c) => !c.salas.length)) continue;
        h = Math.max(p.h, ...cols.map((c) => c.h));
        let x = p.w;
        for (const c of cols) {
          x += hueco;
          let y = 0;
          for (const r of c.salas) {
            salas.push({ p: r, x, y });
            y += r.h + hueco;
          }
          if (h - c.h - hueco > 0) huecos.push({ x, y: c.h + hueco, w: c.w, h: h - c.h - hueco });
          x += c.w;
        }
        w = x;
        // Todo el ancho de las columnas de la derecha, bajo la más alta de ellas (si la principal es más alta).
        const altoDerecha = Math.max(...cols.map((c) => c.h));
        if (h - altoDerecha - hueco > 0) huecos.push({ x: p.w + hueco, y: altoDerecha + hueco, w: w - p.w - hueco, h: h - altoDerecha - hueco });
      }
      salida.push({ salas, comun: null, w, h, compacto });
      if (compacto) continue;
      // Con zona común: en un hueco libre (de pie o tumbada) o, si no, debajo de todo (tumbada).
      const enHueco = (['tumbada', 'de_pie'] as const)
        .flatMap((forma) => huecos.filter((hu) => hu.w >= COMUN[forma].w && hu.h >= COMUN[forma].h).map((hu) => ({ forma, hu })))
        .sort((a, b) => b.hu.w * b.hu.h - a.hu.w * a.hu.h)[0];
      if (enHueco) {
        const { forma, hu } = enHueco;
        const c = COMUN[forma];
        salida.push({ salas, comun: { forma, x: hu.x + Math.round((hu.w - c.w) / 2), y: hu.y + hu.h - c.h, ...c }, w, h, compacto });
      } else {
        // Sin sitio para la zona entera dentro: un rincón de sofás en el hueco (el suelo no queda desnudo)…
        const rincon = huecos.filter((hu) => hu.w >= COMUN.rincon.w && hu.h >= COMUN.rincon.h).sort((a, b) => b.w * b.h - a.w * a.h)[0];
        if (rincon) salida.push({ salas, comun: { forma: 'rincon', x: rincon.x + Math.round((rincon.w - COMUN.rincon.w) / 2), y: rincon.y + Math.round((rincon.h - COMUN.rincon.h) / 2), ...COMUN.rincon }, w, h, compacto });
        // …y la zona entera debajo de todo, tumbada.
        const wc = Math.max(w, COMUN.tumbada.w);
        const desplaza = Math.round((wc - w) / 2);
        salida.push({
          salas: salas.map((s) => ({ ...s, x: s.x + desplaza })),
          comun: { forma: 'tumbada', x: 0, y: h + PASILLO_Y, w: wc, h: ALTO_COMUN },
          w: wc,
          h: h + PASILLO_Y + ALTO_COMUN,
          compacto,
        });
      }
    }
  }
  return salida;
}

/** Cuánto se parece la forma del bloque a la del área visible (0 = igual): lo que llena la pantalla sin suelo desnudo. */
const desajuste = (p: Plano, visW: number, visH: number) => Math.abs(Math.log(p.w / Math.max(1, p.h) / (visW / Math.max(1, visH))));

export function generarMundo(e: EntradaMundo): Mundo {
  // El mayor zoom (3 o 2) con el que el bloque de la oficina cabe en el área visible. A igual zoom gana, por este
  // orden: tener zona común, pasillos normales, más aire alrededor y la forma que mejor llena la pantalla.
  let elegido: { z: number; plano: Plano; aire: number; nota: number[] } | null = null;
  // 10-10 (Pau, revisión de espacio): con filas llenas, una sala de 3 islas solo podía ser 3×1 o 1×3; en 1257 px
  // salía una columna estrecha con suelo vacío a los lados, y en 1560 la segunda sala se salía por abajo. Si con
  // filas llenas no cabe a ningún zoom, antes de desplazar se prueba con filas a medias (3 islas → 2+1): un
  // cuadrante de alfombra vacío (crítica N5) es mejor que media oficina fuera de la pantalla.
  for (const conFilaAMedias of [false, true]) {
    for (const z of conFilaAMedias ? [2] : [3, 2]) {
      for (const compacto of [false, true]) {
        const lista = candidatos(e, compacto, conFilaAMedias);
        for (const aire of [AIRE, 8, AIRE_COMPACTO]) {
          const visW = (e.anchoPx - e.panelIzqPx) / z - 2 * aire;
          const visH = e.altoPx / z - PARED - ZOCALO - 2 * aire;
          for (const plano of lista) {
            if (plano.w > visW || plano.h > visH) continue;
            // 04-11 — tras el zoom y la zona común, que quepa debajo la fila de los que se van (si no, los que salen pisan la sala de
            // abajo). Se puntúa siempre, haya o no alguien saliendo: la oficina no cambia de forma cuando alguien se va.
            const cabeSalida = plano.h + FILA_SALIDA <= visH + aire ? 1 : 0;
            const nota = [z, !plano.comun ? 0 : plano.comun.forma === 'rincon' ? 1 : 2, cabeSalida, compacto ? 0 : 1, aire, -desajuste(plano, visW, visH)];
            if (!elegido || mejor(nota, elegido.nota)) elegido = { z, plano, aire, nota };
          }
        }
      }
      if (elegido) break;
    }
    if (elegido) break;
  }
  // No cabe ni compacta a zoom 2: el mundo crece y se desplaza. Mejor solo hacia abajo (lo natural, con la rueda o
  // el dedo): de lo que cabe a lo ancho, el menos alto (con filas a medias: usar el ancho antes que crecer hacia
  // abajo). Si nada cabe a lo ancho, el que menos se sale en proporción.
  if (!elegido) {
    const visW = (e.anchoPx - e.panelIzqPx) / 2 - 2 * AIRE_COMPACTO;
    const visH = e.altoPx / 2 - PARED - ZOCALO - 2 * AIRE_COMPACTO;
    const exceso = (p: Plano) => Math.max(p.w / visW, p.h / visH);
    const lista = candidatos(e, true, true);
    const aLoAncho = lista.filter((p) => p.w <= visW).sort((a, b) => a.h - b.h);
    const plano = aLoAncho[0] ?? lista.sort((a, b) => exceso(a) - exceso(b))[0]!;
    elegido = { z: 2, plano, aire: AIRE_COMPACTO, nota: [] };
  }
  const { z, plano, aire } = elegido;

  const reservaIzq = Math.ceil(e.panelIzqPx / z);
  // El mundo cubre la ventana; si el bloque no cabe, crece (y la escena se desplaza).
  const ancho = Math.max(Math.ceil(e.anchoPx / z), reservaIzq + plano.w + 2 * aire);
  const altoBase = Math.max(Math.ceil(e.altoPx / z), PARED + ZOCALO + plano.h + 2 * aire);
  const x0 = Math.round(reservaIzq + (ancho - reservaIzq - plano.w) / 2);
  // Centrado en vertical, pero subiendo lo justo para dejar libre la fila de la salida si hay sitio.
  const hBase = altoBase - PARED - ZOCALO;
  const y0 = Math.round(PARED + Math.max(aire, Math.min((hBase - plano.h) / 2, hBase - plano.h - FILA_SALIDA)));
  // 04-11 — si aun así no cabe (oficina baja: la franja de widgets se lleva arriba ~130 px), con alguien saliendo el
  // mundo crece por abajo lo justo para su fila: nada se mueve ni cambia de zoom, sale la barra de desplazar y los que
  // se van no pisan la sala de abajo. Sin nadie saliendo, el mundo es el de siempre.
  const alto = e.saliendo?.length ? Math.max(altoBase, y0 + plano.h + FILA_SALIDA + ZOCALO) : altoBase;
  const visible: Rect = { x: reservaIzq, y: PARED, w: ancho - reservaIzq, h: alto - PARED - ZOCALO };
  const pasilloY = plano.compacto ? PASILLO_Y_COMPACTO : PASILLO_Y;
  const pasilloX = plano.compacto ? PASILLO_X_COMPACTO : PASILLO_X;

  const zonas: ZonaMundo[] = [];
  const islas: Rect[] = [];
  const mesas: MesaMundo[] = [];
  for (const { p, x: px, y: py } of plano.salas) {
    const zx = x0 + px;
    const y = y0 + py;
    // La alfombra empieza bajo la franja del letrero; el letrero, encima de la alfombra (fuera de ella).
    const alfombraSala: Rect = { x: zx, y: y + ALTO_LETRERO, w: p.w, h: p.h - ALTO_LETRERO };
    zonas.push({ projectId: p.sala.projectId, nombre: p.sala.nombre, color: p.sala.color, rect: alfombraSala, letrero: { x: zx, y } });
    const deSala = e.contratados.filter((c) => c.projectId === p.sala.projectId);
    const ocupadas = new Map(deSala.map((c) => [c.mesa, c.agentId]));
    const huecos = huecosVigentes([...ocupadas.keys()], { principal: p.sala.principal, fijadas: p.sala.mesas ?? null, huecos: p.sala.huecos ?? [] }, p.puestos);
    let primeraLibre = -1;
    for (let i = 0; i < p.islas; i += 1) {
      const col = i % p.cols;
      const media = p.media && i === p.islas - 1;
      const fila = Math.floor(i / p.cols);
      const isla: Rect = { x: zx + rellenoDe(pasilloX) + col * (ISLA.w + pasilloX), y: y + ALTO_LETRERO + LETRERO + fila * (ISLA.h + pasilloY), w: ISLA.w, h: media ? CELDA.h : ISLA.h };
      islas.push(isla);
      // Orden de los puestos dentro de la isla: arriba izq., arriba der., abajo izq., abajo der. (media: solo arriba).
      for (let k = 0; k < (media ? 2 : 4); k += 1) {
        const indice = i * 4 + k;
        // Más allá de los puestos de la sala, el resto de la última isla queda como sitio vacío para añadir una mesa
        // con un clic (Pau, 09-10: «hacer clic y añadir una mesa en el espacio que ha quedado hueco»).
        const vacio = indice >= p.puestos;
        if (vacio && indice >= topeMesas(p.sala.principal)) break;
        const arriba = k < 2;
        const celda: Rect = { x: isla.x + (k % 2) * CELDA.w, y: isla.y + (arriba ? 0 : CELDA.h), w: CELDA.w, h: CELDA.h };
        const asiento: Punto = { x: celda.x + CELDA.w / 2, y: arriba ? celda.y + 26 : celda.y + CELDA.h - 26 };
        const agentId = ocupadas.get(indice) ?? null;
        const hueco = vacio || huecos.has(indice);
        if (!agentId && !hueco && primeraLibre < 0) primeraLibre = indice;
        mesas.push({ projectId: p.sala.projectId, indice, rect: celda, asiento, arriba, agentId, libre: !agentId && primeraLibre === indice, hueco, ...(vacio ? { vacio } : {}) });
      }
    }
  }

  // Zona común: reuniones · sofás · cocina, y la recepción (donde se contrata).
  const decor: DecorMundo[] = [];
  let recepcion: Rect | null = null;
  if (plano.comun) {
    const c = { ...plano.comun, x: x0 + plano.comun.x, y: y0 + plano.comun.y };
    if (c.forma === 'tumbada') {
      const reu: Rect = { x: c.x, y: c.y + 4, w: 150, h: 96 };
      const sof: Rect = { x: reu.x + reu.w + 28, y: c.y + 8, w: 132, h: 88 };
      const coc: Rect = { x: c.x + c.w - 112, y: c.y + 4, w: 112, h: 36 };
      recepcion = { x: c.x + c.w - 128, y: c.y + c.h - 44, w: 128, h: 40 };
      decor.push({ tipo: 'reuniones', rect: reu });
      if (sof.x + sof.w <= coc.x - 12) decor.push({ tipo: 'sofas', rect: sof });
      decor.push({ tipo: 'cocina', rect: coc }, { tipo: 'recepcion', rect: recepcion });
    } else if (c.forma === 'rincon') {
      decor.push({ tipo: 'sofas', rect: { x: c.x, y: c.y, w: c.w, h: c.h } });
    } else {
      // De pie: reuniones y sofás arriba; la cocina y la recepción debajo (la recepcionista, entre las dos).
      const reu: Rect = { x: c.x, y: c.y, w: 150, h: 96 };
      const sof: Rect = { x: c.x + c.w - 132, y: c.y + 4, w: 132, h: 88 };
      const coc: Rect = { x: c.x, y: c.y + c.h - 36, w: 112, h: 36 };
      recepcion = { x: c.x + c.w - 128, y: c.y + c.h - 40, w: 128, h: 40 };
      decor.push({ tipo: 'reuniones', rect: reu }, { tipo: 'sofas', rect: sof }, { tipo: 'cocina', rect: coc }, { tipo: 'recepcion', rect: recepcion });
    }
  }
  const puerta: Punto = { x: Math.round((recepcion ? recepcion.x : x0 + plano.w) - 36), y: alto - ZOCALO / 2 };
  // Plantas en las esquinas visibles (el suelo alrededor no queda desnudo).
  const vd = visible.x + visible.w;
  for (const [px, py] of [[vd - 28, PARED + 8], [vd - 28, alto - ZOCALO - 28], [visible.x + 12, alto - ZOCALO - 28], [visible.x + 12, PARED + 8]] as const) {
    decor.push({ tipo: 'planta', rect: { x: px, y: py, w: 14, h: 18 } });
  }

  // 29-10 — la fila de los que se van: a lo largo de la pared de abajo, hacia la izquierda desde la puerta (dentro de lo
  // visible; si no caben, siguen hacia la derecha de la puerta).
  const PASO_SALIDA = 30;
  const ySalida = alto - ZOCALO - 20;
  const salida = (e.saliendo ?? []).map((agentId, i) => {
    const izq = puerta.x - 44 - i * PASO_SALIDA;
    const x = izq >= visible.x + 16 ? izq : puerta.x + 44 + (i - Math.floor((puerta.x - 44 - visible.x - 16) / PASO_SALIDA) - 1) * PASO_SALIDA;
    return { agentId, asiento: { x, y: ySalida } };
  });

  const encuadre: Rect = { x: x0 - aire, y: y0 - aire, w: plano.w + 2 * aire, h: plano.h + 2 * aire };
  const desborda = ancho > Math.ceil(e.anchoPx / z) || alto > Math.ceil(e.altoPx / z);
  return { z, ancho, alto, visible, zonas, islas, mesas, decor, recepcion, puerta, salida, encuadre, desborda };
}

/** Comparación lexicográfica de notas (más es mejor). */
function mejor(a: number[], b: number[]): boolean {
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return a[i]! > b[i]!;
  return false;
}
